import { v4 as uuidv4 } from "uuid";
import { geminiRequest, readJsonFile } from "..";
import i18n from "./i18n";

export const handleDownloadJson = async (values: any, name = "survey-data") => {
  const jsonString = JSON.stringify(values, null, 2);
  const blob = new Blob([jsonString], { type: "application/json" });
  const link = document.createElement("a");
  link.download = `${name}.json`;
  link.href = window.URL.createObjectURL(blob);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// export function handleExtractUIDs(json) {
//   const uidFields = ["uid", "goToPage", "response"];
//   const uidMappings = new Map();

//   function processObject(obj) {
//     if (!obj || typeof obj !== "object") return; // Added to handle null or non-object

//     Object.entries(obj).forEach(([key, value]) => {
//       if (uidFields.includes(key) && typeof value === "string") {
//         const originalUID = value;
//         if (!uidMappings.has(originalUID)) {
//           // Only generate new UID if not already mapped
//           const newUID = uuidv4();
//           uidMappings.set(originalUID, newUID);
//         }
//         obj[key] = uidMappings.get(originalUID);
//       } else if (typeof value === "object") {
//         processObject(value); // Recursion for objects and arrays
//       }
//     });
//   }

//   processObject(json);
//   return uidMappings;
// }

// export function handleExtractUIDs(json) {
//   const uidFields = ["uid", "goToPage", "response"];
//   const uidMappings = new Map();
//   function processObject(obj) {
//     for (const key in obj) {
//       if (uidFields.includes(key)) {
//         const originalUID = obj[key];
//         const newUID = uuidv4();
//         obj[key] = newUID;
//         uidMappings.set(originalUID, newUID);
//       } else if (typeof obj[key] === "object" || Array.isArray(obj[key])) {
//         processObject(obj[key]);
//       }
//     }
//   }

//   processObject(json);
//   return uidMappings;
// }

export function handleExtractUIDs(json: any) {
  const uidFields = ["uid", "goToPage", "response"];
  const uidMappings = new Map<string, string>();

  function processObject(obj: any) {
    if (!obj || typeof obj !== "object" || Array.isArray(obj)) return;

    for (const [key, value] of Object.entries(obj)) {
      if (uidFields.includes(key) && typeof value === "string") {
        if (!uidMappings.has(value)) {
          uidMappings.set(value, uuidv4());
        }
        obj[key] = uidMappings.get(value);
      } else if (typeof value === "object") {
        processObject(value);
      }
    }
  }

  function processArray(arr: any[]) {
    arr.forEach((item) => {
      if (typeof item === "object") {
        processObject(item);
      }
    });
  }

  if (Array.isArray(json)) {
    processArray(json);
  } else {
    processObject(json);
  }

  return uidMappings;
}

export function handleReinsertUIDs(json: any, uidMappings: Map<string, string>) {
  function processObject(obj: any) {
    for (const key in obj) {
      if (typeof obj[key] === "string" && uidMappings.has(obj[key])) {
        obj[key] = uidMappings.get(obj[key]);
      } else if (obj[key] && typeof obj[key] === "object") {
        processObject(obj[key]);
      }
    }
  }

  // Clone the json object to avoid direct modification
  const newJson = JSON.parse(JSON.stringify(json));
  processObject(newJson);
  return newJson;
}

type GeminiResponse = string | Record<string, any>;

export function cleanAndParseAiJson(jsonString: string): GeminiResponse {
  // Check if the string starts with the specific markdown code block pattern for JSON
  if (!jsonString.startsWith("```json")) {
    try {
      // Attempt to parse the string as JSON
      return JSON.parse(jsonString);
    } catch (error) {
      // Log the error if parsing fails
      // Return the original string if it cannot be parsed as JSON
      return jsonString;
    }
  }

  const cleanedString = jsonString.replace(/```json\n|\n```/g, "").trim();
  try {
    const jsonObject = JSON.parse(cleanedString);
    return jsonObject;
  } catch (error) {
    console.error("Error parsing JSON: ", error);
    return jsonString;
  }
}

export function createJsonChunks(
  data: Record<string, string>,
  maxChunkSize = 10000,
): Record<string, string>[] {
  const chunks: Record<string, string>[] = [];
  let currentChunk: Record<string, string> = {};
  let currentChunkSize = 0;

  for (const [id, text] of Object.entries(data)) {
    const itemSize = text.length; // Approximate size
    if (
      currentChunkSize + itemSize > maxChunkSize &&
      Object.keys(currentChunk).length > 0
    ) {
      chunks.push(currentChunk);
      currentChunk = {};
      currentChunkSize = 0;
    }
    currentChunk[id] = text;
    currentChunkSize += itemSize;
  }

  if (Object.keys(currentChunk).length > 0) {
    chunks.push(currentChunk);
  }

  return chunks;
}

export function extractReinsertableContent(
  json: Record<string, any>,
  includedKeys: string[],
): [string, string][] {
  const reinsertableContent: [string, string][] = [];
  let index = 0;

  function processObject(obj: Record<string, any>) {
    for (const key in obj) {
      if (
        !includedKeys.includes(key) &&
        isNaN(Number(key)) &&
        !Array.isArray(obj[key])
      ) {
        continue;
      }

      if (obj[key] === "" || obj[key] === null) {
        continue;
      }

      if (typeof obj[key] === "string") {
        const id = `reinsertionId-${index}`;
        reinsertableContent.push([id, obj[key]]);
        obj[key] = id;
        index++;
      } else if (typeof obj[key] === "object") {
        console.log(obj[key]);
        processObject(obj[key]);
      }
    }
  }

  processObject(json);
  return reinsertableContent;
}

export function reinsertReinsertableContent(
  json: Record<string, any>,
  translations: Record<string, string>,
): Record<string, any> {
  function processObject(obj: Record<string, any>) {
    for (const key in obj) {
      if (
        typeof obj[key] === "string" &&
        obj[key].startsWith("reinsertionId-")
      ) {
        const translated = translations[obj[key]];
        if (translated) {
          obj[key] = translated;
        }
      } else if (typeof obj[key] === "object" && obj[key] !== null) {
        processObject(obj[key]);
      }
    }
  }

  processObject(json);
  return json;
}

export const handleAiTransformData = async (
  assistantId: string,
  inputPrompt: string,
  includedKeys: string[],
  toBeAiTransformedJSON: Record<string, any>,
  handleSubmit: (data: Record<string, any>) => void,
  setLoading: (loading: boolean) => void,
): Promise<void> => {
  setLoading(true);
  const aiTransformableStringsArray = extractReinsertableContent(
    toBeAiTransformedJSON,
    includedKeys,
  );
  const aiTransformableStringsObject = Object.fromEntries(
    aiTransformableStringsArray,
  );
  const jsonChunks = createJsonChunks(aiTransformableStringsObject);

  try {
    const allTranslations: Record<string, string> = {};
    let retryCount = 0;
    const maxRetries = 4;

    for (const chunk of jsonChunks) {
      let translatedChunk: any;
      let isValidResponse = false;

      while (!isValidResponse && retryCount < maxRetries) {
        const prompt = `${inputPrompt}. Please translate the values of the following JSON object, returning a valid JSON object with the identical keys. Do not alter the keys or the structure of the JSON. Only translate the string values:\n\n${JSON.stringify(
          chunk,
          null,
          2,
        )}`;
        
        const result = await geminiRequest({
          body: prompt,
          assistantId: assistantId,
          lang: i18n?.resolvedLanguage || "en",
        });

        if (result) {
          translatedChunk = cleanAndParseAiJson(result);

          if (
            typeof translatedChunk === "object" &&
            translatedChunk !== null &&
            !Array.isArray(translatedChunk)
          ) {
            const originalKeys = Object.keys(chunk);
            const translatedKeys = Object.keys(translatedChunk);
            if (
              originalKeys.length === translatedKeys.length &&
              originalKeys.every((key) => translatedKeys.includes(key))
            ) {
              isValidResponse = true;
            } else {
              console.warn("AI returned JSON with mismatched keys, retrying...");
              retryCount++;
            }
          } else {
            console.warn("AI did not return a valid JSON object, retrying...");
            retryCount++;
          }
        } else {
          retryCount++;
        }
      }

      if (!isValidResponse) {
        console.error("Failed to get valid AI response after maximum retries");
        translatedChunk = chunk;
      }

      Object.assign(allTranslations, translatedChunk);
    }

    const aiTransformedData = reinsertReinsertableContent(
      toBeAiTransformedJSON,
      allTranslations,
    );

    const newSurvey = {
      ...aiTransformedData,
      createdAt: new Date().toISOString(),
    };

    handleSubmit(newSurvey);
    setLoading(false);
  } catch (error) {
    setLoading(false);
    console.error("Translation error:", error);
  }
};

export function handleGetFieldsToInclude(variant: "survey" | "infoPage" | "") {
  return variant === "survey"
    ? ["title", "callToParticipate", "pages", "createdAt"]
    : variant === "infoPage"
      ? ["title", "brief", "content", "image", "createdAt"]
      : [];
}
export async function handleInsertJSONTemplate(
  files: FileList | null,
  data: any,
  variant: "survey" | "infoPage" | "",
) {
  const fieldsToInclude = handleGetFieldsToInclude(variant);

  let readJsonData = data ? await data : null;

  if (!readJsonData) {
    if (files && files[0] && files[0].name.endsWith(".json")) {
      readJsonData = await readJsonFile(files[0]);
    } else {
      return;
    }
  }
  if (!readJsonData) return;

  console.log(variant, fieldsToInclude);
  // Filter jsonData to include only the specified fields
  const filteredData = fieldsToInclude.reduce(
    (acc: Record<string, any>, field) => {
      if (readJsonData[field] !== undefined) {
        acc[field] = readJsonData[field];
      }
      return acc;
    },
    {},
  );

  console.log(filteredData);

  // Extract UIDs and replace them
  const uidMappings = handleExtractUIDs(filteredData);
  const newData = handleReinsertUIDs(filteredData, uidMappings);

  // Prepare updated data for Formik, preserving specific values
  const updatedFormikValues = {
    ...newData,
    title: `${newData?.title} (${i18n.t("global:copied")})`,
    createdAt: new Date().toISOString(),
  };

  return updatedFormikValues;
}
